import { Box } from "@mui/material";
import { grey } from "@mui/material/colors";
import type { PointerEvent, ReactNode } from "react";
import { useEffect, useRef, useState } from "react";

export interface Size {
  widthPx: number;
  heightPx: number;
}

interface Props {
  size: Size;
  minWidth: number;
  maxWidth: number;
  minHeight: number;
  maxHeight: number;
  onSizeChange: (size: Size) => void;
  onCommit: (size: Size) => void;
  children: ReactNode;
}

const HANDLE_SIZE = 14;

const clamp = (value: number, min: number, max: number) => Math.max(min, Math.min(max, value));

const ResizableCornerWrapper = ({
  size,
  minWidth,
  maxWidth,
  minHeight,
  maxHeight,
  onSizeChange,
  onCommit,
  children,
}: Props) => {
  const dragStartRef = useRef<{ clientX: number; clientY: number; startWidth: number; startHeight: number } | null>(
    null
  );
  const pendingSizeRef = useRef<Size | undefined>(undefined);
  const animationFrameRef = useRef<number | undefined>(undefined);
  const [dragging, setDragging] = useState(false);

  useEffect(() => {
    return () => {
      if (animationFrameRef.current !== undefined) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = undefined;
      }
      pendingSizeRef.current = undefined;
    };
  }, []);

  const computeSize = (clientX: number, clientY: number): Size => {
    const start = dragStartRef.current;
    if (!start) {
      return size;
    }
    return {
      widthPx: clamp(start.startWidth + (clientX - start.clientX), minWidth, maxWidth),
      heightPx: clamp(start.startHeight + (clientY - start.clientY), minHeight, maxHeight),
    };
  };

  const handlePointerDown = (event: PointerEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.currentTarget.setPointerCapture(event.pointerId);
    dragStartRef.current = {
      clientX: event.clientX,
      clientY: event.clientY,
      startWidth: size.widthPx,
      startHeight: size.heightPx,
    };
    setDragging(true);
  };

  const handlePointerMove = (event: PointerEvent<HTMLDivElement>) => {
    if (!dragStartRef.current || !event.currentTarget.hasPointerCapture(event.pointerId)) {
      return;
    }
    pendingSizeRef.current = computeSize(event.clientX, event.clientY);
    if (animationFrameRef.current !== undefined) {
      return;
    }

    // Let MUI's positioning and input effects settle between continuous pointer updates.
    animationFrameRef.current = requestAnimationFrame(() => {
      animationFrameRef.current = undefined;
      const nextSize = pendingSizeRef.current;
      pendingSizeRef.current = undefined;
      if (nextSize && (nextSize.widthPx !== size.widthPx || nextSize.heightPx !== size.heightPx)) {
        onSizeChange(nextSize);
      }
    });
  };

  const finishDrag = (event: PointerEvent<HTMLDivElement>) => {
    if (animationFrameRef.current !== undefined) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = undefined;
    }
    pendingSizeRef.current = undefined;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    if (dragStartRef.current !== null) {
      const nextSize = computeSize(event.clientX, event.clientY);
      dragStartRef.current = null;
      if (nextSize.widthPx !== size.widthPx || nextSize.heightPx !== size.heightPx) {
        onSizeChange(nextSize);
      }
      onCommit(nextSize);
    }
    setDragging(false);
  };

  const gripColor = dragging ? grey[600] : grey[400];
  const gripHoverColor = grey[600];
  const gripGradient = (color: string) =>
    `linear-gradient(135deg, transparent 0, transparent 40%, ${color} 40%, ${color} 55%, transparent 55%, transparent 70%, ${color} 70%, ${color} 85%, transparent 85%)`;

  return (
    <Box
      sx={{
        position: "relative",
        width: `${size.widthPx}px`,
        height: `${size.heightPx}px`,
        display: "flex",
        flexDirection: "column",
      }}
    >
      {children}
      <Box
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={finishDrag}
        onPointerCancel={finishDrag}
        role="separator"
        aria-label="Resize report list"
        sx={{
          position: "absolute",
          right: 0,
          bottom: 0,
          width: `${HANDLE_SIZE}px`,
          height: `${HANDLE_SIZE}px`,
          cursor: "nwse-resize",
          userSelect: "none",
          touchAction: "none",
          zIndex: 2,
          "&::before": {
            content: '""',
            position: "absolute",
            right: "3px",
            bottom: "3px",
            width: "8px",
            height: "8px",
            background: gripGradient(gripColor),
            transition: "background 150ms",
          },
          "&:hover::before": {
            background: gripGradient(gripHoverColor),
          },
        }}
      />
    </Box>
  );
};

export default ResizableCornerWrapper;
